// ScoringManagement.tsx - Main scoring configuration management component

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/useToast';
import { ScoringConfiguration, ScoringPreset } from '../../types/ScoringConfiguration';
import { ScoringConfigurationService } from '../../services/ScoringConfigurationService';
import { StandardButton } from '../ui';
import { ScoringConfigList } from './ScoringConfigList';
import { ScoringConfigEditor } from './ScoringConfigEditor';
import { ScoringPresetSelector } from './ScoringPresetSelector';
import { ScoringTestPanel } from './ScoringTestPanel';
import { LoadingSpinner } from '../ui/LoadingSpinner';

export const ScoringManagement: React.FC = () => {
  const { isDark } = useTheme();
  const { showToast } = useToast();
  
  const [configurations, setConfigurations] = useState<ScoringConfiguration[]>([]);
  const [activeConfig, setActiveConfig] = useState<ScoringConfiguration | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<ScoringConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'list' | 'edit' | 'create' | 'test'>('list');
  const [showPresets, setShowPresets] = useState(false);

  // Load configurations on mount
  useEffect(() => {
    loadConfigurations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const [configs, active] = await Promise.all([
        ScoringConfigurationService.getAll(),
        ScoringConfigurationService.getActive()
      ]);
      setConfigurations(configs);
      setActiveConfig(active);
    } catch (error) {
      console.error('Error loading configurations:', error);
      showToast({ 
        title: 'Error loading scoring configurations', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedConfig(null);
    setMode('create');
  };

  const handleEdit = (config: ScoringConfiguration) => {
    setSelectedConfig(config);
    setMode('edit');
  };

  const handleTest = (config: ScoringConfiguration) => {
    setSelectedConfig(config);
    setMode('test');
  };

  const handleSave = async (config: ScoringConfiguration) => {
    try {
      await ScoringConfigurationService.save(config);
      showToast({ 
        title: mode === 'create' ? 'Configuration created' : 'Configuration updated', 
        type: 'success' 
      });
      await loadConfigurations();
      setMode('list');
      setSelectedConfig(null);
    } catch (error) {
      showToast({ 
        title: error instanceof Error ? error.message : 'Error saving configuration', 
        type: 'error' 
      });
    }
  };

  const handleDelete = async (configId: string) => {
    try {
      await ScoringConfigurationService.delete(configId);
      showToast({ 
        title: 'Configuration deleted', 
        type: 'success' 
      });
      await loadConfigurations();
    } catch (error) {
      showToast({ 
        title: error instanceof Error ? error.message : 'Error deleting configuration', 
        type: 'error' 
      });
    }
  };

  const handleSetActive = async (configId: string) => {
    try {
      await ScoringConfigurationService.setActive(configId);
      showToast({ 
        title: 'Active configuration updated', 
        type: 'success' 
      });
      await loadConfigurations();
    } catch (error) {
      showToast({ 
        title: 'Error updating active configuration', 
        type: 'error' 
      });
    }
  };

  const handleApplyPreset = async (preset: ScoringPreset) => {
    try {
      const config = await ScoringConfigurationService.applyPreset(preset);
      showToast({ 
        title: 'Preset applied successfully', 
        type: 'success' 
      });
      await loadConfigurations();
      setShowPresets(false);
      handleEdit(config);
    } catch (error) {
      showToast({ 
        title: 'Error applying preset', 
        type: 'error' 
      });
    }
  };

  const handleExport = async (config: ScoringConfiguration) => {
    try {
      const exportData = await ScoringConfigurationService.export(config.id);
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scoring-config-${config.name.toLowerCase().replace(/\s+/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast({ 
        title: 'Configuration exported', 
        type: 'success' 
      });
    } catch (error) {
      showToast({ 
        title: 'Error exporting configuration', 
        type: 'error' 
      });
    }
  };

  const handleImport = async (file: File) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const imported = await ScoringConfigurationService.import(data);
      showToast({ 
        title: 'Configuration imported successfully', 
        type: 'success' 
      });
      await loadConfigurations();
      handleEdit(imported);
    } catch (error) {
      showToast({ 
        title: 'Error importing configuration', 
        type: 'error' 
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Scoring Configuration
          </h2>
          <p className={`mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Manage how the game calculates scores and grades
          </p>
        </div>

        {mode === 'list' && (
          <div className="flex gap-3">
            <StandardButton
              onClick={() => setShowPresets(true)}
              variant="secondary"
              leftIcon="🎯"
            >
              Apply Preset
            </StandardButton>
            <StandardButton
              onClick={handleCreate}
              variant="primary"
              leftIcon="➕"
            >
              Create Configuration
            </StandardButton>
          </div>
        )}

        {mode !== 'list' && (
          <StandardButton
            onClick={() => {
              setMode('list');
              setSelectedConfig(null);
            }}
            variant="secondary"
            leftIcon="←"
          >
            Back to List
          </StandardButton>
        )}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {mode === 'list' && (
          <motion.div
            key="list"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <ScoringConfigList
              configurations={configurations}
              activeConfigId={activeConfig?.id || ''}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onSetActive={handleSetActive}
              onTest={handleTest}
              onExport={handleExport}
              onImport={handleImport}
            />
          </motion.div>
        )}

        {(mode === 'edit' || mode === 'create') && (
          <motion.div
            key="editor"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <ScoringConfigEditor
              configuration={selectedConfig}
              mode={mode === 'create' ? 'create' : 'edit'}
              onSave={handleSave}
              onCancel={() => {
                setMode('list');
                setSelectedConfig(null);
              }}
            />
          </motion.div>
        )}

        {mode === 'test' && selectedConfig && (
          <motion.div
            key="test"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <ScoringTestPanel
              configuration={selectedConfig}
              onClose={() => {
                setMode('list');
                setSelectedConfig(null);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preset Selector Modal */}
      <AnimatePresence>
        {showPresets && (
          <ScoringPresetSelector
            onSelect={handleApplyPreset}
            onClose={() => setShowPresets(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};